import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClientStore } from '@/stores/client';
import {
  CREATIVE_WORKBENCH_STORAGE_KEY,
  useWorkbenchStore,
} from '@/stores/workbench';

const LOCAL_STORAGE_DEBOUNCE_MS = 500;

export type DateWindow = '7d' | '14d' | '30d' | '90d';

export interface DateRange {
  window: DateWindow;
  from: string;
  to: string;
}

export interface CreativeWorkbenchContextValue {
  clientUid: string | null;
  personaId: string | null;
  dateRange: DateRange;
  filterPreset: string | null;
  // Chat-analytics-ui — persists the analyst chat thread across navigations
  // within the Performance View. Mirrored into the URL `analyst_thread` param
  // by `EmbeddedChatPanel`.
  analystThreadId: string | null;

  setClient: (uid: string | null) => void;
  setPersona: (id: string | null) => void;
  setDateRange: (range: DateRange) => void;
  setFilterPreset: (preset: string | null) => void;
  setAnalystThreadId: (id: string | null) => void;
}

const CreativeWorkbenchContext = createContext<CreativeWorkbenchContextValue | null>(
  null,
);

function computeDefaultRange(win: DateWindow): DateRange {
  const days = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 }[win];
  const now = new Date();
  const from = new Date(now);
  from.setDate(now.getDate() - days);
  return {
    window: win,
    from: from.toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

function isValidWindow(w: string | null | undefined): w is DateWindow {
  return w === '7d' || w === '14d' || w === '30d' || w === '90d';
}

interface PersistedState {
  clientUid: string | null;
  personaId: string | null;
  window: DateWindow;
  filterPreset: string | null;
}

function readPersisted(): PersistedState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CREATIVE_WORKBENCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      clientUid: parsed.clientUid ?? null,
      personaId: parsed.personaId ?? null,
      window: isValidWindow(parsed.window) ? parsed.window : '30d',
      filterPreset: parsed.filterPreset ?? null,
    };
  } catch {
    return null;
  }
}

export function CreativeWorkbenchProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedClientUid: storeClientUid, selectClient: storeSelectClient } = useClientStore();

  // Priority: URL param > cookie (store) > localStorage.
  const [clientUid, setClientUidState] = useState<string | null>(() => {
    const fromUrl = searchParams.get('client');
    if (fromUrl !== null) return fromUrl || null;
    if (storeClientUid) return storeClientUid;
    return readPersisted()?.clientUid ?? null;
  });
  const [personaId, setPersonaIdState] = useState<string | null>(() => {
    const fromUrl = searchParams.get('persona');
    if (fromUrl !== null) return fromUrl || null;
    return readPersisted()?.personaId ?? null;
  });
  const [dateRange, setDateRangeState] = useState<DateRange>(() => {
    const urlWindow = searchParams.get('window');
    const urlFrom = searchParams.get('from');
    const urlTo = searchParams.get('to');
    const baseWindow: DateWindow = isValidWindow(urlWindow)
      ? urlWindow
      : readPersisted()?.window ?? '30d';
    const range = computeDefaultRange(baseWindow);
    return {
      window: baseWindow,
      from: urlFrom || range.from,
      to: urlTo || range.to,
    };
  });
  const [filterPreset, setFilterPresetState] = useState<string | null>(() => {
    const fromUrl = searchParams.get('preset');
    if (fromUrl !== null) return fromUrl || null;
    return readPersisted()?.filterPreset ?? null;
  });
  const [analystThreadId, setAnalystThreadIdState] = useState<string | null>(() => {
    return searchParams.get('analyst_thread');
  });

  // TopBar / QA clears `?persona=` while context state must follow (TopBar is outside this provider).
  const personaUrlSyncRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const p = searchParams.get('persona');
    const normalized = p && p !== '' ? p : null;
    if (personaUrlSyncRef.current === undefined) {
      personaUrlSyncRef.current = normalized;
      return;
    }
    if (personaUrlSyncRef.current !== normalized) {
      personaUrlSyncRef.current = normalized;
      setPersonaIdState(normalized);
      useWorkbenchStore.getState().setPersonaId(normalized);
    }
  }, [searchParams]);

  // Keep URL search params in sync on change (replace mode — no history pollution).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const assign = (key: string, value: string | null) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    };
    assign('client', clientUid);
    assign('persona', personaId);
    assign('window', dateRange.window);
    assign('from', dateRange.from);
    assign('to', dateRange.to);
    assign('preset', filterPreset);
    assign('analyst_thread', analystThreadId);

    // Only write if something changed — avoid replace storms.
    let changed = false;
    const current = searchParams.toString();
    const proposed = next.toString();
    if (current !== proposed) changed = true;
    if (changed) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid, personaId, dateRange.window, dateRange.from, dateRange.to, filterPreset, analystThreadId]);

  // Persist to localStorage, debounced.
  const persistTimer = useRef<number | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (persistTimer.current !== null) {
      window.clearTimeout(persistTimer.current);
    }
    persistTimer.current = window.setTimeout(() => {
      try {
        const persisted: PersistedState = {
          clientUid,
          personaId,
          window: dateRange.window,
          filterPreset,
        };
        window.localStorage.setItem(
          CREATIVE_WORKBENCH_STORAGE_KEY,
          JSON.stringify(persisted),
        );
      } catch {
        // Quota / private mode — silent.
      }
    }, LOCAL_STORAGE_DEBOUNCE_MS);
    return () => {
      if (persistTimer.current !== null) {
        window.clearTimeout(persistTimer.current);
      }
    };
  }, [clientUid, personaId, dateRange.window, filterPreset]);

  // Keep the global workbench store in sync so TopBar (outside the provider) can read it.
  useEffect(() => {
    useWorkbenchStore.getState().setPersonaId(personaId);
  }, [personaId]);

  // When the global ClientSelector changes its selection, pull it into this context.
  useEffect(() => {
    if (storeClientUid && storeClientUid !== clientUid) {
      setClientUidState(storeClientUid);
    }
  }, [storeClientUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const setClient = useCallback(
    (uid: string | null) => {
      setClientUidState(uid);
      storeSelectClient(uid); // keep cookie in sync
    },
    [storeSelectClient],
  );
  const setPersona = useCallback(
    (id: string | null) => {
      setPersonaIdState(id);
      useWorkbenchStore.getState().setPersonaId(id);
    },
    [],
  );
  const setDateRange = useCallback((range: DateRange) => setDateRangeState(range), []);
  const setFilterPreset = useCallback(
    (preset: string | null) => setFilterPresetState(preset),
    [],
  );
  const setAnalystThreadId = useCallback(
    (id: string | null) => setAnalystThreadIdState(id),
    [],
  );

  const value = useMemo<CreativeWorkbenchContextValue>(
    () => ({
      clientUid,
      personaId,
      dateRange,
      filterPreset,
      analystThreadId,
      setClient,
      setPersona,
      setDateRange,
      setFilterPreset,
      setAnalystThreadId,
    }),
    [
      clientUid,
      personaId,
      dateRange,
      filterPreset,
      analystThreadId,
      setClient,
      setPersona,
      setDateRange,
      setFilterPreset,
      setAnalystThreadId,
    ],
  );

  return (
    <CreativeWorkbenchContext.Provider value={value}>
      {children}
    </CreativeWorkbenchContext.Provider>
  );
}

export function useCreativeWorkbench(): CreativeWorkbenchContextValue {
  const ctx = useContext(CreativeWorkbenchContext);
  if (!ctx) {
    throw new Error(
      'useCreativeWorkbench must be used within a CreativeWorkbenchProvider',
    );
  }
  return ctx;
}
